import React from 'react';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon, 
  CheckCircleIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  ShieldCheckIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

const HowItWorks = () => {

  const steps = [
    {
      number: '01',
      title: 'Choose a Skill Challenge',
      description: 'Select from carefully designed, industry-relevant competitions built to reflect real-world problem statements.',
      icon: <SparklesIcon className="h-7 w-7" />,
      color: 'from-blue-600 to-indigo-600',
      details: [
        'Structured problem briefs',
        'Transparent evaluation criteria',
        'Nominal participation fee'
      ]
    },
    {
      number: '02',
      title: 'Participate Individually or as a Team',
      description: 'Work independently or collaborate with peers. Our system supports structured teamwork and individual submissions.',
      icon: <UserGroupIcon className="h-7 w-7" />,
      color: 'from-indigo-500 to-purple-600',
      details: [
        'Team formation tools',
        'Shared dashboards',
        'Collaborative submission workflow'
      ]
    },
    {
      number: '03',
      title: 'Structured Evaluation Process',
      description: 'Submissions are assessed through a structured review mechanism combining technical benchmarks and mentor oversight.',
      icon: <AcademicCapIcon className="h-7 w-7" />,
      color: 'from-blue-500 to-cyan-500',
      details: [
        'Code & logic review',
        'Efficiency benchmarking',
        'Performance insights report'
      ]
    },
    {
      number: '04',
      title: 'Verified Credentials',
      description: 'Participants receive verifiable digital certificates. Top performers earn distinction-based recognition.',
      icon: <ShieldCheckIcon className="h-7 w-7" />,
      color: 'from-emerald-500 to-teal-600',
      details: [
        'Digital verification system',
        'Professional shareable credentials',
        'Tier-based recognition'
      ]
    },
    {
      number: '05',
      title: 'Performance-Based Rewards',
      description: 'High-performing participants may qualify for stipends or sponsored reward pools based on evaluation results.',
      icon: <CurrencyRupeeIcon className="h-7 w-7" />,
      color: 'from-orange-500 to-yellow-600',
      details: [
        'Structured reward allocation',
        'Transparent ranking system',
        'Merit-based distribution'
      ]
    },
    {
      number: '06',
      title: 'Industry Exposure Opportunities',
      description: 'Outstanding performers may unlock short-term internship pathways with our partner network.',
      icon: <BriefcaseIcon className="h-7 w-7" />,
      color: 'from-indigo-600 to-blue-700',
      details: [
        'Interview shortlisting support',
        '1–3 month pathways',
        'Mentor-led professional exposure'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-28 pb-20">

      {/* Hero Section */}
      <section className="py-16 px-6 text-center max-w-5xl mx-auto">
        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6">
          How It <span className="text-indigo-500">Works</span>
        </h1>
        <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
          Our structured evaluation ecosystem bridges practical skills with measurable recognition.
          From participation to professional exposure, every stage is designed with clarity,
          transparency, and industry alignment.
        </p>
      </section>

      {/* Steps Section */}
      <section className="py-10 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-[#0b0b0b] border border-white/5 p-8 rounded-2xl hover:border-indigo-500/30 transition-colors duration-300"
            >
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6`}>
                {step.icon}
              </div>

              <div className="text-xs text-indigo-500 font-semibold tracking-widest mb-2">
                STEP {step.number}
              </div>

              <h3 className="text-xl font-semibold mb-3">
                {step.title}
              </h3>

              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {step.description}
              </p>

              <div className="space-y-3 border-t border-white/5 pt-4">
                {step.details.map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <CheckCircleIcon className="h-4 w-4 text-indigo-500" />
                    <span className="text-xs text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value Section */}
      <section className="py-24 px-6 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          
          <div>
            <h2 className="text-4xl font-bold mb-8">
              Built for <span className="text-indigo-500">Credibility</span>
            </h2>

            <div className="space-y-8">
              <ValueItem 
                title="Transparent Evaluation"
                desc="Clear assessment metrics ensure fairness and measurable outcomes for every participant."
              />
              <ValueItem 
                title="Verified Digital Credentials"
                desc="Achievements are recorded within a structured verification framework."
              />
              <ValueItem 
                title="Industry Connectivity"
                desc="Top performers gain structured exposure to relevant hiring and internship opportunities."
              />
            </div>
          </div>

          <div className="bg-[#0b0b0b] border border-white/10 p-10 rounded-3xl">
            <h4 className="text-sm text-gray-400 tracking-widest mb-6">
              Outcome Summary
            </h4>

            <div className="space-y-4">
              <OutcomeRow text="Participation Certificate" />
              <OutcomeRow text="Performance Report" />
              <OutcomeRow text="Internship Shortlisting Eligibility" highlight />
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-8">
          Ready to Participate?
        </h2>

        <Link 
          to="/competitions"
          className="inline-block px-10 py-4 bg-white text-black font-semibold rounded-xl hover:bg-indigo-600 hover:text-white transition-colors duration-300"
        >
          View Active Challenges
        </Link>

        <p className="mt-6 text-gray-500 text-sm">
          Skill-based • Transparent Evaluation • Merit-Driven Recognition
        </p>
      </section>

    </div>
  );
};

const ValueItem = ({ title, desc }) => (
  <div>
    <h4 className="font-semibold text-lg mb-2">
      {title}
    </h4>
    <p className="text-gray-400 text-sm leading-relaxed">
      {desc}
    </p>
  </div>
);

const OutcomeRow = ({ text, highlight }) => (
  <div className={`h-14 rounded-xl flex items-center px-6 justify-between border ${
    highlight 
      ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400' 
      : 'bg-white/5 border-white/10 text-gray-400'
  }`}>
    <span className="text-sm">{text}</span>
    <CheckCircleIcon className="h-5 w-5" />
  </div>
);

export default HowItWorks;